import * as React from "react";
import IRestrictedView from "./IRestrictedView";
import moment from "moment";

import VerifyCertify from "@market/components/VerifyCertify/VerifyCertify";
import SMAccessValidationTypes from "@market/enums/SMAccessValidationTypes";
import Countdown from "@appComponents/Countdown/Countdown";
import { RestrictedContainer } from "@appComponents/RestrictedContainer/RestrictedContainer";
import { classNames } from "@features/utils/reactFunctions";

import * as style from "./RestrictedViewStyle.module.scss";
import * as general from "@appStyles/general.module.scss";
import * as elements from "@appStyles/_elements.module.scss";
import { rootUrl } from "@bundles/consts";

const RestrictedView: React.FunctionComponent<IRestrictedView> = (props) => {
  let view: JSX.Element;

  const message = () => {
    switch (props.reason) {
      case SMAccessValidationTypes.restricted_investing_cayman:
      case SMAccessValidationTypes.restricted_investing_primary_market:
        view = (
          <div>
            <h3 className={style.header}>
              Investing is restricted on your account
            </h3>
            <h4>
              Please contact BF{" "}
              <a href="/support" className={style.supportUrl}>
                support
              </a>{" "}
              for more information.
            </h4>
          </div>
        );
        break;
      case SMAccessValidationTypes.sell_restricted_by_admin:
      case SMAccessValidationTypes.buy_restricted_by_admin:
      case SMAccessValidationTypes.sell_restricted_by_pep_sanction:
      case SMAccessValidationTypes.buy_restricted_by_pep_sanction:
        view = (
          <div>
            <h3 className={style.header}>
              Investing is restricted on your account
            </h3>
            <h4>
              Investing is restricted on your account. Please contact BF Support
              for more information.
            </h4>
          </div>
        );
        break;
      case SMAccessValidationTypes.sell_restricted_by_user_statement:
      case SMAccessValidationTypes.buy_restricted_by_user_statement:
        view = (
          <div>
            <h3 className={style.header}>
              Investing is restricted on your account
            </h3>
            <h4>
              Your account is restricted from trading securities because, during
              your Tier 1 BFID session (Suitability), you selected that you do
              not wish to trade securities.
              <br />
              <br />
              To unlock access, you can complete a new Tier 1 session on BFID
              and update your response regarding securities investments,
              including your predicted trading frequency and annual investment
              amount.
            </h4>
          </div>
        );
        break;
      case SMAccessValidationTypes.buy_restricted_by_risk_rating:
      case SMAccessValidationTypes.sell_restricted_by_risk_rating:
        view = (
          <div>
            <h3 className={style.header}>
              Investing is restricted on your account
            </h3>
            <h4>
              Your account is currently restricted from investing due to an
              ongoing Enhanced Due Diligence (EDD) review. Please complete your
              EDD session on BFID if you haven’t already.
              <br />
              <br />
              If you have completed it, your review may still be pending
              compliance approval.
            </h4>
          </div>
        );
        break;
      case SMAccessValidationTypes.sell_restricted_by_tier:
      case SMAccessValidationTypes.buy_restricted_by_tier:
        view = (
          <div>
            <h3 className={style.header}>
              Investing is restricted on your account
            </h3>
            <h4>
              Trading securities is currently available only to Tier 4 users, as
              Cayman Islands regulations require proof of net assets of $1M or
              more.
              <br />
              <br />
              If this applies to you, please complete a Tier 4 session on BFID.
              <br />
              <br />
              We are also working on opening the secondary market to Tier 3
              users under a new BF entity in Mauritius soon.
            </h4>
          </div>
        );
        break;
      case SMAccessValidationTypes.sell_restricted_by_country_uk:
      case SMAccessValidationTypes.buy_restricted_by_country_uk:
        view = (
          <div>
            <h3 className={style.header}>
              Investing is restricted on your account
            </h3>
            <h4>
              At this time, we cannot offer this function to UK residents. We
              are in the process of obtaining approval from the UK regulator and
              will update UK users once BF is authorised to allow secondary
              market trading again.
            </h4>
          </div>
        );
        break;
      case SMAccessValidationTypes.sell_restricted_by_2fa:
      case SMAccessValidationTypes.buy_restricted_by_2fa:
        const securityUrl = `${rootUrl}/profile/${props.investor_slug}/account?tab=security`;
        view = (
          <div>
            <h3 className={style.header}>
              Investing is restricted on your account
            </h3>
            <h4>
              Your account is restricted from trading securities because
              2-Factor Authentication (2FA) is not set up. To unlock access,
              please enable 2FA in your profile’s{" "}
              <a
                href={securityUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                security settings
              </a>
              .
            </h4>
          </div>
        );
        break;
        break;
      case SMAccessValidationTypes.is_us_resident:
        view = (
          <div>
            <h3 className={style.header}>Purchases unavailable in your area</h3>
            <h4 className={style.textStyle}>
              Currently, equity shares transactions on the secondary market are
              unavailable for investors from United States.
              <br />
              <br />
              We are working on obtaining an SEC-regulated ATS license to remove
              this limitation as soon as possible.
            </h4>
            <div className={general.mT2}>
              <a
                className={[elements.btn, elements.btnSuccess].join(" ")}
                href={`/market`}
              >
                Back to market
              </a>
            </div>
          </div>
        );
        break;
      case SMAccessValidationTypes.aml_failed:
        view = (
          <div>
            <h2>Your profile has failed an automated AML check.</h2>
            <h4>
              Please{" "}
              <a href="/support" className={style.supportUrl}>
                contact BF Support
              </a>{" "}
              for more info.
            </h4>
          </div>
        );
        break;

      case SMAccessValidationTypes.not_certified:
      case SMAccessValidationTypes.not_verified:
        view = <VerifyCertify />;
        break;

      case SMAccessValidationTypes.has_no_2fa_enabled:
        view = (
          <div>
            <h3 className={style.header}>Two Factor Authentication required</h3>
            <h4 className={style.textStyle}>
              <a
                href={`/profile/${props.investor_slug}/account?tab=security`}
                className={style.supportUrl}
                title="2fa settings"
              >
                Set up two-factor authentication for your account.
              </a>
            </h4>
          </div>
        );
        break;

      case SMAccessValidationTypes.has_active_sell_order:
      case SMAccessValidationTypes.has_active_buy_order:
        const type =
          props.reason === SMAccessValidationTypes.has_active_sell_order
            ? "sell"
            : "buy";
        view = (
          <div>
            <h3 className={style.header}>{`${type}`} order already placed.</h3>
            <h4 className={style.textStyle}>
              Currently you can have only one open {`${type}`} order per asset.
            </h4>
            <div className={general.mT2}>
              <br />
            </div>
          </div>
        );
        break;

      case SMAccessValidationTypes.no_shares_for_sell:
        view = (
          <div>
            <h3 className={style.header}>No shares available</h3>
            <h4 className={style.textStyle}>
              Currently you have no sellable shares
            </h4>
          </div>
        );
        break;

      default:
        view = (
          <div>
            <h4 className={style.textStyle}>
              Unfortunately you are unauthorised to trade this shares. Please
              contact our &nbsp;
              <a href="/support" className={style.supportUrl} title="Support">
                support team
              </a>{" "}
              for more information.
            </h4>
          </div>
        );
    }
    return view;
  };

  return (
    <div
      className={classNames(
        style.container,
        general.pX1,
        general.pY1,
        general.mB2,
        general.textCenter
      )}
    >
      <RestrictedContainer>{message()}</RestrictedContainer>
    </div>
  );
};

export default RestrictedView;
